import React, {lazy} from 'react';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import history from 'shared/history';
import {mobileAccountSettingsRoutesForPortal} from 'src/components/AccountSettings/utils/mobile-account-settings-portal-component-map';
import MyList from 'src/components/CollegeSearch/MyList';
import SimpleMobilePageNavbar from 'src/components/mobile-navbar/simple-mobile-page-navbar';
import Survey from 'src/components/Survey';
import withSuspense from 'src/hooks/withSuspense';
import PortfolioSidebar from '../../../high-school-student/portfolio-sidebar/components/portfolio-sidebar';
import MobileApplication, {
  MobileRedirectConfig,
  MobileRouteConfig,
} from '../../components/mobile-application';
import {withEducationPhase} from '../../hooks/with-education-phase';
import {EducationPhase, UserType} from '../../types/enums';
import bottomNavbarConfig from './bottomNavbarConfig';
import ModalRoutes from './ModalRoutes';
import {MobileApplicationLoaderProps} from '../../components/mobile-application/mobile-application';

const EarningsSidebarPages = lazy(() => import('../../components/earnings-sidebar-pages'));
const TabbedCollegeSearch = lazy(() => import('src/components/CollegeSearch/TabbedCollegeSearch'));

const CollegeProfile = lazy(() => import('transfer-student/college-profile/containers'));
const CopyrightPolicy = lazy(() => import('src/components/copyright-policy'));
const DataViewHistory = lazy(() => import('src/components/data-view-history-page'));
const NativeInvitationsContainer = lazy(
  () => import('src/components/AccountSettings/native-invitations/native-invitations-container')
);
const NotificationCenter = lazy(() => import('shared/notification-center'));
const PrivacyPolicy = lazy(() => import('src/components/privacy-policy'));
const Rules = lazy(() => import('student-rules/components/rules'));
const Sitemap = lazy(() => import('src/components/Sitemap'));
const StudentMessages = lazy(() => import('src/components/StudentMessages'));
const TermsOfUse = lazy(() => import('src/components/TermsOfUse'));
const Videos = lazy(() => import('src/components/Videos'));
const Unsubscribe = lazy(() => import('src/components/Unsubscribe/Unsubscribe'));

const StudentSitemap = withSuspense(Sitemap, {type: 'Student'});
const TransferEarningsSidebarPages = withEducationPhase(
  EarningsSidebarPages,
  EducationPhase.COMMUNITY_COLLEGE
);

export default function TransferMobileApplication({store}: MobileApplicationLoaderProps) {
  const {push} = history;

  const routes: (MobileRouteConfig | MobileRedirectConfig)[] = [
    ...mobileAccountSettingsRoutesForPortal(UserType.CC_STUDENT),
    {
      path: '/checklist',
      component: TransferEarningsSidebarPages,
      navbar: <SimpleMobilePageNavbar title="Checklist" />,
      color: 'light',
    },
    {
      path: '/colleges/my-list',
      component: MyList,
      navbar: <SimpleMobilePageNavbar title="My List" />,
      color: 'light',
    },
    {
      path: '/colleges',
      component: TabbedCollegeSearch,
      navbar: <SimpleMobilePageNavbar title="Colleges" />,
      color: 'primary',
    },
    {path: '/colleges-who-have-viewed-me', component: DataViewHistory},
    {path: '/copyright_policy', component: CopyrightPolicy},
    {path: '/edu/:slug', component: CollegeProfile},
    {path: '/following', component: PortfolioSidebar},
    {
      path: '/home',
      component: TransferEarningsSidebarPages,
      color: 'light',
      navbar: (
        <SimpleMobilePageNavbar
          title="Home"
          button={{
            onClick: () => {
              push('/portfolio');
            },
            text: 'Portfolio',
          }}
        />
      ),
    },
    {
      path: '/invitations',
      component: NativeInvitationsContainer,
      navbar: <SimpleMobilePageNavbar subpage title="Invitations" />,
    },
    {
      path: '/messages/:messageId?',
      component: StudentMessages,
      navbar: <SimpleMobilePageNavbar subpage title="Messages" />,
    },
    {
      path: '/notification-center',
      component: NotificationCenter,
      navbar: <SimpleMobilePageNavbar subpage title="Notifications" />,
    },

    {
      path: '/portfolio',
      component: TransferEarningsSidebarPages,
      navbar: <SimpleMobilePageNavbar title="Portfolio" />,
      color: 'primary',
    },
    {path: '/privacy-policy', component: PrivacyPolicy},
    {path: '/sitemap', component: Sitemap, exact: true},
    {path: '/sitemap/students', component: StudentSitemap, exact: true},
    {path: '/students/rules', component: Rules},
    {path: '/survey/:id/:page?', component: Survey},
    {path: '/terms-of-use', component: TermsOfUse},
    {path: '/unsubscribe', component: Unsubscribe},
    {path: '/videos', component: Videos, navbar: <SimpleMobilePageNavbar subpage title="Videos" />},
    // Redirects
    {path: '/notification-center-refresh', to: '/notification-center', as: 'redirect'},
    {path: '/transfers/settings', to: '/account-settings', as: 'redirect'},
    {as: 'redirect', to: '/home'},
  ];

  return (
    <MobileApplication
      routes={routes}
      store={store}
      modalRoutes={<ModalRoutes />}
      bottomNavbarConfig={bottomNavbarConfig}
      morePage={{portal: UserType.CC_STUDENT, awardAmount: 50}}
      hasAwardsNotifications
    />
  );
}
